import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Collapsible, CollapsibleContent } from '@/components/shadcn/collapsible'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'
import { Switch } from '@/components/shadcn/switch'
import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { MultiSelectComboboxField } from '@/components/form-components/MultiSelectCombobox'

import { useCurrentApplication } from '../useCurrentApplication'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { Separator } from '@/components/shadcn/separator'

// Define REST API types using RestSchemas
type MessagesAgentExistingAgent = RestSchemas['Messages.Agent.ExistingAgent']
type MessagesAgentNewAgent = RestSchemas['Messages.Agent.NewAgent']

const baseFormSchema = z.object({
  key: z.string().min(1, 'Key is required').max(60, 'Key cannot exceed 10 characters'),
  name: z.string().min(1, 'Name is required').max(60, 'Name cannot exceed 60 characters'),
  description: z.string().max(256, 'Description cannot exceed 256 characters').optional(),
  providerId: z.preprocess(
    (value) => (value === null || value === '' || value === undefined ? undefined : value),
    z.number().int().positive().optional()
  ),
  model: z.string().optional().default(''),
  temperature: z.coerce.number().min(0).max(1).optional().default(0),
  maxTokens: z.coerce.number().min(1).optional().default(1),
  toolIds: z
    .preprocess((value) => {
      if (Array.isArray(value)) {
        return value.map((item) => Number(item)).filter((item) => Number.isFinite(item))
      }
      if (typeof value === 'string') {
        return value
          .split(',')
          .map((item) => Number(item.trim()))
          .filter((item) => Number.isFinite(item))
      }
      return []
    }, z.array(z.number().int().positive()))
    .optional()
    .default([]),
  systemPrompt: z.string().optional().default(''),
  locked: z.boolean().default(false),
  contextManagementEnabled: z.boolean().default(false),
  contextWindow: z.coerce.number().int().min(0).optional().default(0),
  compactorTokenThresholdPct: z.coerce.number().min(0.05).max(0.95).optional().default(0.7),
  compactorMessageThreshold: z.coerce.number().int().min(1).optional().default(40),
  compactorKeepRecent: z.coerce.number().int().min(1).optional().default(20),
  compactorSummaryMaxTokens: z.coerce.number().int().min(1).optional().default(800),
  compactorModel: z.preprocess(
    (value) => (value === null || value === undefined ? '' : value),
    z.string().optional().default('')
  ),
  langchainConfig: z.string().default('{}'),
  promptTemplate: z.string().default(''),
})

const formSchema = baseFormSchema.superRefine((data, ctx) => {
  if (!data.providerId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Provider is required',
      path: ['providerId'],
    })
  }

  if (!data.model.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Model is required',
      path: ['model'],
    })
  }

  if (!data.contextManagementEnabled) {
    return
  }

  if (data.compactorKeepRecent >= data.compactorMessageThreshold) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Must be less than the message threshold',
      path: ['compactorKeepRecent'],
    })
  }
})

const DEFAULT_CONTEXT_MANAGEMENT_FIELDS = {
  contextManagementEnabled: false,
  contextWindow: 0,
  compactorTokenThresholdPct: 0.7,
  compactorMessageThreshold: 40,
  compactorKeepRecent: 20,
  compactorSummaryMaxTokens: 800,
  compactorModel: '',
} as const

const buildContextManagementPayload = (data: TAgentForm) => {
  if (!data.contextManagementEnabled) {
    return { ...DEFAULT_CONTEXT_MANAGEMENT_FIELDS }
  }

  return {
    contextManagementEnabled: true as const,
    contextWindow: data.contextWindow,
    compactorTokenThresholdPct: data.compactorTokenThresholdPct,
    compactorMessageThreshold: data.compactorMessageThreshold,
    compactorKeepRecent: data.compactorKeepRecent,
    compactorSummaryMaxTokens: data.compactorSummaryMaxTokens,
    compactorModel: data.compactorModel?.trim() ?? '',
  }
}

export type TAgentForm = z.infer<typeof baseFormSchema>

const toSnakeCase = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')

// Props interface
type AgentFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedAgent?: MessagesAgentExistingAgent
}

export const AgentForm = (props: AgentFormProps) => {
  const { applicationId } = useCurrentApplication()
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedAgent

  // REST API client - use createRestClient() pattern
  const { getAgentApiGetAgent, postAgentApiUpdateAgent, putAgentApiAddAgent } = useRestClient()

  // Update mutation
  const { mutate: updateAgent, isPending: isUpdating } = postAgentApiUpdateAgent.useMutation(undefined, {
    onSuccess: () => {
      getAgentApiGetAgent.invalidateQueries({})
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Agent successfully updated')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error updating agent')
    },
  })

  // Create mutation
  const { mutate: addAgent, isPending: isAdding } = putAgentApiAddAgent.useMutation(undefined, {
    onSuccess: () => {
      getAgentApiGetAgent.invalidateQueries({})
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Agent successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding agent')
    },
  })

  // Submit handler — providerKey and tools are resolved from registry IDs in FormComponent
  const submitHandler = useCallback(
    (data: TAgentForm, providerKey: string, tools: string[]) => {
      const contextFields = buildContextManagementPayload(data)

      if (edit && props.selectedAgent) {
        const updatedAgent: MessagesAgentExistingAgent = {
          id: props.selectedAgent.id,
          key: data.key,
          name: data.name,
          description: data.description || '',
          providerKey,
          model: data.model,
          temperature: data.temperature,
          maxTokens: data.maxTokens,
          tools,
          systemPrompt: data.systemPrompt,
          locked: data.locked,
          ...contextFields,
        }
        updateAgent({
          body: updatedAgent,
          path: { applicationId: applicationId },
        })
      } else {
        const newAgent: MessagesAgentNewAgent = {
          key: data.key,
          name: data.name,
          description: data.description || '',
          providerKey,
          model: data.model ?? '',
          temperature: data.temperature ?? 0,
          maxTokens: data.maxTokens ?? 1,
          tools,
          systemPrompt: data.systemPrompt ?? '',
          locked: data.locked,
          ...contextFields,
        }
        addAgent({
          body: newAgent,
          path: { applicationId: applicationId },
        })
      }
    },
    [edit, props.selectedAgent, applicationId, addAgent, updateAgent]
  )

  const isLoading = isUpdating || isAdding

  if (isLoading) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} submitHandler={submitHandler} />
  )
}

// Form Component
const FormComponent = (
  props: AgentFormProps & {
    submitHandler: (data: TAgentForm, providerKey: string, tools: string[]) => void
  }
) => {
  const { getProviderRegistrationApiListProviders, getToolRegistrationApiListTools } = useRestClient()
  const { data: providers } = getProviderRegistrationApiListProviders.useQuery(undefined)
  const { data: availableTools } = getToolRegistrationApiListTools.useQuery(undefined)

  const selectedAgent: Partial<MessagesAgentExistingAgent> = useMemo(
    () => props.selectedAgent ?? {},
    [props.selectedAgent]
  )
  const {
    key,
    name,
    description,
    locked,
    providerKey,
    model,
    temperature,
    maxTokens,
    tools,
    systemPrompt,
    contextManagementEnabled,
    contextWindow,
    compactorTokenThresholdPct,
    compactorMessageThreshold,
    compactorKeepRecent,
    compactorSummaryMaxTokens,
    compactorModel,
  } = selectedAgent
  const edit = !!props.selectedAgent

  const providerOptions = useMemo(
    () =>
      (providers ?? []).map((p) => ({
        value: p.id,
        label: p.name,
        description: p.description ?? undefined,
      })),
    [providers]
  )

  const toolOptions = useMemo(
    () =>
      (availableTools ?? [])
        .filter((tool) => tool.functionName)
        .map((tool) => ({
          value: tool.id,
          label: tool.name,
          description: tool.functionName ?? undefined,
          group: tool.moduleName ?? 'Uncategorized',
        })),
    [availableTools]
  )

  const resolvedProviderId = useMemo(() => {
    if (!providerKey) return undefined
    return (providers ?? []).find((p) => p.key === providerKey)?.id
  }, [providerKey, providers])

  const resolvedToolIds = useMemo(() => {
    if (!tools?.length) return []

    const toolIdByFunctionName = new Map(
      (availableTools ?? [])
        .filter((tool) => tool.functionName)
        .map((tool) => [tool.functionName!, tool.id])
    )
    return tools
      .map((functionName) => toolIdByFunctionName.get(functionName))
      .filter((id): id is number => typeof id === 'number')
  }, [availableTools, tools])

  const defaultValues = useMemo(() => {
    return {
      key: key ?? '',
      name: name ?? '',
      description: description ?? '',
      locked: locked ?? false,
      providerId: resolvedProviderId,
      model: model ?? '',
      temperature: temperature ?? 0.5,
      maxTokens: maxTokens ?? 1500,
      toolIds: resolvedToolIds,
      systemPrompt: systemPrompt ?? '',
      contextManagementEnabled: contextManagementEnabled ?? false,
      contextWindow: contextWindow ?? 0,
      compactorTokenThresholdPct: compactorTokenThresholdPct ?? 0.7,
      compactorMessageThreshold: compactorMessageThreshold ?? 40,
      compactorKeepRecent: compactorKeepRecent ?? 20,
      compactorSummaryMaxTokens: compactorSummaryMaxTokens ?? 800,
      compactorModel: compactorModel ?? '',
    }
  }, [
    key,
    name,
    description,
    locked,
    resolvedProviderId,
    model,
    temperature,
    maxTokens,
    resolvedToolIds,
    systemPrompt,
    contextManagementEnabled,
    contextWindow,
    compactorTokenThresholdPct,
    compactorMessageThreshold,
    compactorKeepRecent,
    compactorSummaryMaxTokens,
    compactorModel,
  ])

  const form = useForm<TAgentForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
    shouldUnregister: false,
  })

  const selectedProviderId = form.watch('providerId')
  const contextManagementEnabledWatch = form.watch('contextManagementEnabled')
  const watchedName = form.watch('name')
  const watchedKey = form.watch('key')
  const keyWasEdited = !!form.formState.dirtyFields.key

  const selectedProvider = useMemo(
    () => (providers ?? []).find((p) => p.id === selectedProviderId),
    [providers, selectedProviderId]
  )

  const modelOptions = useMemo(
    () =>
      (selectedProvider?.models ?? []).map((providerModel) => ({
        value: providerModel.model,
        label: providerModel.name,
      })),
    [selectedProvider]
  )

  const allModelOptions = useMemo(
    () =>
      (providers ?? []).flatMap((provider) =>
        (provider.models ?? []).map((providerModel) => ({
          value: providerModel.model,
          label: providerModel.name,
          group: provider.name,
        }))
      ),
    [providers]
  )

  useEffect(() => {
    const currentModel = form.getValues('model')
    if (!selectedProviderId) {
      if (currentModel) {
        form.setValue('model', '', { shouldValidate: true })
      }
      return
    }

    if (currentModel && !modelOptions.some((option) => option.value === currentModel)) {
      form.setValue('model', '', { shouldValidate: true })
    }
  }, [form, modelOptions, selectedProviderId])

  useEffect(() => {
    if (edit || keyWasEdited) return

    const generatedKey = toSnakeCase(watchedName ?? '').slice(0, 10)
    if (generatedKey !== watchedKey) {
      form.setValue('key', generatedKey, { shouldDirty: false, shouldTouch: false, shouldValidate: true })
    }
  }, [edit, form, keyWasEdited, watchedKey, watchedName])

  const handleInvalidSubmit = useCallback(() => {
    toast.error('Please fix validation errors before submitting')
  }, [])

  const handleSubmit = useCallback(
    (data: TAgentForm) => {
      const provider = (providers ?? []).find((p) => p.id === data.providerId)
      if (!provider?.key) {
        toast.error('Provider could not be resolved')
        return
      }

      const toolFunctionNames = (data.toolIds ?? [])
        .map((toolId) => (availableTools ?? []).find((tool) => tool.id === toolId)?.functionName)
        .filter((functionName): functionName is string => !!functionName)

      if ((data.toolIds ?? []).length > toolFunctionNames.length) {
        toast.error('One or more selected tools are missing a function name')
        return
      }

      props.submitHandler(data, provider.key, toolFunctionNames)
    },
    [availableTools, props, providers]
  )

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit, handleInvalidSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <SingleSelectComboboxField
                control={form.control}
                name="providerId"
                label="Provider *"
                options={providerOptions}
                placeholder="Select a provider..."
                searchPlaceholder="Search providers..."
                emptyMessage="No providers found."
              />
              <SingleSelectComboboxField
                control={form.control}
                name="model"
                label="Model *"
                options={modelOptions}
                placeholder="Select a model..."
                searchPlaceholder="Search models..."
                emptyMessage={
                  selectedProviderId !== undefined ? 'No models found for this provider.' : 'Select a provider first.'
                }
                disabled={selectedProviderId === undefined}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Agent Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Agent Description</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Agent Key *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="temperature"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Temperature</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} max={1} step={0.1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maxTokens"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Tokens</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} step={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <MultiSelectComboboxField
                control={form.control}
                name="toolIds"
                label="Tools"
                options={toolOptions}
                placeholder="Select tools..."
                searchPlaceholder="Search tools..."
                emptyMessage="No tools found."
              />
              <FormField
                control={form.control}
                name="systemPrompt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>System Prompt</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contextManagementEnabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border bg-background p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Context management</FormLabel>
                      <FormDescription>
                        When enabled, older messages are compacted to stay within the model context window. When
                        disabled, the full chat history is sent on every turn.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={(checked) => field.onChange(checked)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Collapsible open={contextManagementEnabledWatch}>
                <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                  <div className="space-y-6 rounded-lg border-l-[10px] border-l-tangarine-300 bg-tangarine-50 p-5">
                  <SingleSelectComboboxField
                    control={form.control}
                    name="compactorModel"
                    label="Compactor model"
                    description="Model used to write summaries. Leave empty to use the main agent model, or pick a cheaper model to reduce cost."
                    options={allModelOptions}
                    placeholder="Use main agent model"
                    searchPlaceholder="Search models..."
                    emptyMessage="No models found."
                  />
                  <div className="overflow-hidden rounded-lg bg-background">
                    <div className="bg-tangarine-100 p-5">
                      <h4 className="text-base font-bold text-muted-foreground">Trigger gates</h4>
                      <p className="text-sm text-muted-foreground">Compaction runs when either threshold is reached.</p>
                    </div>
                    <div className="space-y-4 p-5">
                      <FormField
                        control={form.control}
                        name="compactorTokenThresholdPct"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Token threshold</FormLabel>
                            <FormControl>
                              <Input type="number" min={0.05} max={0.95} step={0.05} {...field} />
                            </FormControl>
                            <FormDescription>
                              Fraction of the model context window at which to compact. Lower values compact more
                              aggressively.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="compactorMessageThreshold"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message threshold</FormLabel>
                            <FormControl>
                              <Input type="number" min={1} step={1} {...field} />
                            </FormControl>
                            <FormDescription>
                              Compact after this many unsummarised messages, even when token usage is still low.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="contextWindow"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Context window override</FormLabel>
                            <FormControl>
                              <Input type="number" min={0} step={1} {...field} />
                            </FormControl>
                            <FormDescription>
                              Override the model context-window size. Use 0 to rely on the built-in lookup (for example,
                              gpt-4.1 maps to about 1M tokens).
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-lg bg-background">
                    <div className="bg-tangarine-100 p-5">
                      <h4 className="text-base font-bold text-muted-foreground">Compaction shape</h4>
                    </div>
                    <div className="space-y-4 p-5">
                      <FormField
                        control={form.control}
                        name="compactorKeepRecent"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Keep recent messages</FormLabel>
                            <FormControl>
                              <Input type="number" min={1} step={1} {...field} />
                            </FormControl>
                            <FormDescription>
                              Most-recent messages always kept verbatim. Must be less than the message threshold.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="compactorSummaryMaxTokens"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Summary max tokens</FormLabel>
                            <FormControl>
                              <Input type="number" min={1} step={1} {...field} />
                            </FormControl>
                            <FormDescription>Maximum length of the summariser output.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
              <Separator />
              <FormField
                control={form.control}
                name="locked"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border bg-background p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Locked</FormLabel>
                      <div className="text-sm text-muted-foreground">Lock this agent to prevent modifications</div>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="form-action-container">
              <Button type="submit">{edit ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
